/** The admin service calls a content preview needs (a subset of the admin BackendService). */
export interface ContentPreviewService {
  setupAssetStorageWithRetry(forceRetrieve: boolean): Promise<{ token: { token: string } }>;
  getAsset(
    path: string,
    token: string,
    options: { url?: boolean; listItems?: boolean; base64?: false },
  ): Promise<unknown>;
  getAssetByURL(
    path: string,
    options: { url?: boolean; listItems?: boolean; base64?: false },
  ): Promise<unknown>;
  getAssetPresignedURL(path: string): Promise<string>;
  getGlobalConfiguration(): Promise<unknown>;
  getVideoPlaybackUrl(externalId: string): Promise<unknown>;
}

/** Adds what the fund opportunity page needs while it loads. */
export interface FundOpportunityPreviewService extends ContentPreviewService {
  checkFundOpportunityPDFAvailable(id: string): Promise<boolean>;
}

export type PreviewHandlers = Record<string, (...args: unknown[]) => Promise<unknown>>;

/** Handed to the iframe in place of a storage token: it is never used to fetch anything. */
export const PREVIEW_ASSET_TOKEN = { token: { token: 'preview' } };

type AssetOptions = { url?: boolean; listItems?: boolean; base64?: false };
const assetOptions = (options: unknown): AssetOptions =>
  typeof options === 'object' && options !== null ? (options as AssetOptions) : {};

/**
 * The read-only calls content blocks make while rendering (images, videos, global config),
 * answered by the admin page's own services. The iframe never receives a credential: it gets a
 * placeholder storage token, and asset reads run here with the admin's real one.
 */
export function createContentPreviewHandlers(service: ContentPreviewService): PreviewHandlers {
  return {
    setupAssetStorageWithRetry: async () => PREVIEW_ASSET_TOKEN,
    getAsset: async (path, _ignoredPreviewToken, options) => {
      const { token } = await service.setupAssetStorageWithRetry(false);
      return service.getAsset(String(path), token.token, assetOptions(options));
    },
    getAssetByURL: async (path, options) =>
      service.getAssetByURL(String(path), assetOptions(options)),
    getAssetPresignedURL: async (path) => service.getAssetPresignedURL(String(path)),
    getGlobalConfiguration: async () => service.getGlobalConfiguration(),
    getVideoPlaybackUrl: async (externalId) => service.getVideoPlaybackUrl(String(externalId)),
  };
}

/**
 * The content calls plus what the investor fund opportunity page makes on load. Custodian status
 * and storage readiness are per-investor checks with no admin equivalent, so the preview answers
 * them as for an ordinary investor; EOI actions (submit, withdraw, share…) stay refused.
 */
export function createFundOpportunityPreviewHandlers(
  service: FundOpportunityPreviewService,
): PreviewHandlers {
  return {
    ...createContentPreviewHandlers(service),
    checkFundOpportunityPDFAvailable: async (id) =>
      service.checkFundOpportunityPDFAvailable(String(id)),
    getCustodianStatus: async () => ({ isCustodian: false }),
    waitForAssetStorage: async () => true,
  };
}
